<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { signIn, confirmSignIn, getCurrentUser } from "aws-amplify/auth";
import QRCode from "qrcode";

const router = useRouter();
const qrDataUrl = ref("");
const email = ref("");
const password = ref("");
const code = ref("");
const newPwd = ref("");
const step = ref<"login" | "newPassword" | "totpSetup" | "totpConfirm">("login");
const totpSecret = ref("");
const totpUri = ref("");
const error = ref("");
const busy = ref(false);

onMounted(async () => {
  try { await getCurrentUser(); router.push("/producten"); } catch { /* niet ingelogd */ }
});

async function handleNext(nextStep: { signInStep: string; totpSetupDetails?: { sharedSecret: string; getSetupUri: (app: string, user: string) => URL } }) {
  const s = nextStep?.signInStep;
  if (s === "DONE") return router.push("/producten");
  if (s === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP" && nextStep.totpSetupDetails) {
    totpSecret.value = nextStep.totpSetupDetails.sharedSecret;
    totpUri.value = nextStep.totpSetupDetails.getSetupUri("binker admin", email.value).toString();
    qrDataUrl.value = await QRCode.toDataURL(totpUri.value, { margin: 1, width: 200 });
    step.value = "totpSetup";
    return;
  }
  if (s === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") { step.value = "totpConfirm"; return; }
  if (s === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") { step.value = "newPassword"; return; }
  error.value = `Onverwachte stap: ${s}`;
}

async function doNewPassword() {
  error.value = ""; busy.value = true;
  try {
    const { nextStep } = await confirmSignIn({ challengeResponse: newPwd.value });
    await handleNext(nextStep);
  } catch (e) { error.value = (e as Error)?.message || "Wachtwoord afwijzen."; }
  finally { busy.value = false; }
}

async function doLogin() {
  error.value = ""; busy.value = true;
  try {
    const { nextStep } = await signIn({ username: email.value, password: password.value });
    await handleNext(nextStep);
  } catch (e) { error.value = (e as Error)?.message || "Inloggen mislukt."; }
  finally { busy.value = false; }
}
async function doConfirm() {
  error.value = ""; busy.value = true;
  try {
    const { nextStep } = await confirmSignIn({ challengeResponse: code.value });
    await handleNext(nextStep);
  } catch (e) { error.value = (e as Error)?.message || "Code onjuist."; }
  finally { busy.value = false; }
}
</script>

<template>
  <div class="wrap">
    <div class="card">
      <div class="brand">binker<span class="dot">.</span> <span class="admin">admin</span></div>

      <template v-if="step === 'login'">
        <input v-model="email" type="email" placeholder="E-mail" class="inp" @keyup.enter="doLogin" />
        <input v-model="password" type="password" placeholder="Wachtwoord" class="inp" @keyup.enter="doLogin" />
        <button class="btn" :disabled="busy" @click="doLogin">{{ busy ? '…' : 'Inloggen' }}</button>
      </template>

      <template v-else-if="step === 'newPassword'">
        <p class="hint">Eerste login: kies een nieuw wachtwoord (min. 12 tekens, hoofd-/kleine letters, cijfer, symbool).</p>
        <input v-model="newPwd" type="password" placeholder="Nieuw wachtwoord" class="inp" @keyup.enter="doNewPassword" />
        <button class="btn" :disabled="busy" @click="doNewPassword">{{ busy ? '…' : 'Wachtwoord instellen' }}</button>
      </template>

      <template v-else-if="step === 'totpSetup'">
        <p class="hint">Scan deze QR-code met je authenticator-app (Google Authenticator, 1Password, Authy…) en vul daarna de 6-cijferige code in.</p>
        <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR-code voor authenticator" class="qr" />
        <details class="manual">
          <summary>Liever handmatig? Voer deze sleutel in</summary>
          <code class="secret">{{ totpSecret }}</code>
        </details>
        <input v-model="code" placeholder="6-cijferige code" class="inp" @keyup.enter="doConfirm" />
        <button class="btn" :disabled="busy" @click="doConfirm">{{ busy ? '…' : 'Bevestigen' }}</button>
      </template>

      <template v-else>
        <p class="hint">Vul de code uit je authenticator-app in.</p>
        <input v-model="code" placeholder="6-cijferige code" class="inp" @keyup.enter="doConfirm" />
        <button class="btn" :disabled="busy" @click="doConfirm">{{ busy ? '…' : 'Bevestigen' }}</button>
      </template>

      <p v-if="error" class="err">{{ error }}</p>
    </div>
  </div>
</template>

<style scoped>
.wrap { min-height: 100vh; display: grid; place-items: center; padding: 20px; }
.card { width: 100%; max-width: 360px; background: var(--surface); border: 1px solid var(--hairline); border-radius: 16px; padding: 28px; display: flex; flex-direction: column; gap: 12px; }
.brand { font-family: var(--font-ui); font-weight: 800; font-size: 26px; letter-spacing: -0.02em; margin-bottom: 8px; }
.dot { color: var(--coral); }
.admin { font-weight: 600; font-size: 15px; color: var(--muted); }
.inp { padding: 12px 14px; border: 1px solid var(--border-input); border-radius: 10px; background: var(--warm); font-size: 15px; font-family: inherit; }
.btn { padding: 13px; border: 0; border-radius: 999px; background: var(--coral); color: #fff; font-weight: 700; font-size: 15px; cursor: pointer; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hint { color: var(--mid); font-size: 13px; line-height: 1.4; margin: 0; }
.secret { display: block; background: var(--warm); padding: 10px; border-radius: 8px; font-size: 13px; word-break: break-all; margin-top: 8px; }
.qr { display: block; width: 200px; height: 200px; margin: 4px auto; border-radius: 8px; }
.manual { font-size: 13px; color: var(--mid); }
.manual summary { cursor: pointer; }
.err { color: var(--red); font-size: 13px; }
</style>
